<template>
  <div
    :class="['item-list-container row items-center q-py-sm', bgColor]"
    :data-testid="`atendimento-${attendance.id}`"
    @click="$emit('onClick')"
    @dblclick="$emit('onDblClick')"
  >
    <component
      :is="iconItem"
      :class="['col-auto q-px-sm', colorItem, { 'icon-chat': isText }]"
    >
      <q-tooltip>
        {{ statusDirection }}
      </q-tooltip>
    </component>

    <div class="col">
      <div class="column">
        <div
          class="col label-contact ellipsis"
          style="max-width: 100px"
        >
          <span
            :class="['q-pa-none', 'q-pa-none', textColor, { 'no-select': isVoice && isAnonymizationEnabled }]"
          >
            {{ contact }}
            <q-tooltip v-if="isVoice && isAnonymizationEnabled">
              Número anonimizado por política de segurança
            </q-tooltip>

            <q-tooltip v-if="isText">
              {{ contactTooltip }}
            </q-tooltip>
          </span>
        </div>

        <div
          v-if="isVoice"
          class="col-auto label-status"
        >
          <span
            v-if="showStatus"
            :class="['q-pa-none', 'q-pa-none', textColor]"
          >
            {{ statusText }}
          </span>

          <TimerWrapper
            v-if="initialDate"
            :class="['label-time', textColor]"
            :created-at="initialDate"
            :finished-at="attendance.hangupAt"
          />
        </div>

        <div
          v-if="isEmail"
          class="col-auto label-status"
        >
          <span
            :class="['ellipsis-2-lines max-width-170', textColor]"
            v-html="mailStatus"
          />
        </div>

        <div
          v-if="isText"
          class="col-auto label-status"
        >
          <span :class="['ellipsis-2-lines max-width-170', textColor]">
            {{ lastMessage }}
          </span>
        </div>
      </div>
    </div>

    <div class="col-auto q-px-sm text-right">
      <div :class="['column label-time full-height', textColor]">
        <div class="row ">
          <div>
            {{ attendanceDate }}
          </div>
          <div
            v-if="canMarkAsUnread"
            :class="['q-ml-xs', 'hover-actions', { 'is-menu-open': isUnreadMenuOpen }]"
            style="margin-right: 1px;"
            @click.stop
          >
            <q-btn
              flat
              dense
              round
              color="grey-7"
              icon="mdi-chevron-down"
              data-testid="atendimento-menu"
              size="5px"
            >
              <AttendanceReadUnreadMenu
                :can-mark-as-unread="canMarkAsUnread"
                :has-manual-unread="hasManualUnread"
                @show="isUnreadMenuOpen = true"
                @hide="isUnreadMenuOpen = false"
                @mark-as-unread="handleMarkAsUnread"
                @mark-as-read="handleMarkAsRead"
              />
            </q-btn>
          </div>
        </div>
        <div
          v-if="hasBadgeWithNumber"
          class="col-auto"
        >
          <q-badge
            rounded
            v-if="hasBadgeWithNumber"
            color="orange"
            v-bind="unreadBadgeProps"
            style="min-width: 14px; min-height: 14px;"
          />
        </div>

        <div
          v-if="hasBadgeEmpty"
          class="col-auto"
        >
          <q-badge
            rounded
            color="orange"
            style="min-width: 14px; min-height: 14px;"
          />
        </div>

        <div
          v-if="isPending"
          :class="['col-auto', textColorPending]"
        >
          Pendente
        </div>
      </div>
    </div>

    <AttendanceReadUnreadMenu
      v-if="canMarkAsUnread"
      context-menu
      touch-position
      :can-mark-as-unread="canMarkAsUnread"
      :has-manual-unread="hasManualUnread"
      @mark-as-unread="handleMarkAsUnread"
      @mark-as-read="handleMarkAsRead"
    />
  </div>
</template>

<script>
import Helpers, { getContactNumber } from '@/libs/helpers'
import { DIRECTION_IN, DIRECTION_AUTO, DIRECTION_OUT } from '@/constants/directions'
import CallActiveDark from '@/assets/icons/activeDark.svg'
import CallReceptiveDark from '@/assets/icons/receptiveDark.svg'
import CallRejectedDark from '@/assets/icons/rejectedDark.svg'
import MessageInIcon from '@/assets/icons/messageIn.svg'
import MessageOutIcon from '@/assets/icons/messageOut.svg'
import MailInIcon from '@/assets/icons/mail-in.svg'
import MailOutIcon from '@/assets/icons/mail-out.svg'
import IconCallOngoingAuto from '@/assets/icons/call-ongoing-auto.svg'
import IconCallOngoingOut from '@/assets/icons/call-ongoing-out.svg'
import IconCallOngoingIn from '@/assets/icons/call-ongoing-in.svg'
import IconCallAnsweredAuto from '@/assets/icons/call-answered-auto.svg'
import IconCallAnsweredOut from '@/assets/icons/call-answered-out.svg'
import IconCallAnsweredIn from '@/assets/icons/call-answered-in.svg'
import IconCallRejectedOut from '@/assets/icons/call-rejected-out.svg'
import TimerWrapper from '@/components/TimerWrapper'
import AttendanceReadUnreadMenu from '@/components/AttendanceReadUnreadMenu'
import { VOICE_CHANNEL, TEXT_CHANNEL, MAIL_CHANNEL } from '@/constants/channels'
import { DISCARDED, COMPLETED, ANSWERING, OFFERING, SEND_DONE, LOSED } from '@/constants/callStatus'
import { mapActions, mapState, mapGetters } from 'vuex'
import { anonymizePhone } from '@/components/utils/phones'
import { parseContactInfo, contactDisplayName, contactInfoTooltip } from '@/libs/contactInfo'

export default {
  components: {
    AttendanceReadUnreadMenu,
    TimerWrapper,
    CallActiveDark,
    CallReceptiveDark,
    CallRejectedDark
  },

  props: {
    attendance: {}
  },

  data () {
    return {
      isUnreadMenuOpen: false
    }
  },

  watch: {
    'attendance.id' () {
      this.isUnreadMenuOpen = false
    },

    'attendance.status' () {
      this.isUnreadMenuOpen = false
    },

    canMarkAsUnread (canMarkAsUnread) {
      if (!canMarkAsUnread) {
        this.isUnreadMenuOpen = false
      }
    }
  },

  computed: {
    ...mapState({
      profilers: ({ attendanceModule }) => attendanceModule.profilers
    }),

    ...mapGetters({
      queueProperties: 'agentModule/queueProperties'
    }),

    queueId () {
      return this.attendance?.queueId ?? this.attendance?.queue
    },

    isAnonymizationEnabled () {
      if (this.attendance?.shouldAnonymize) return true
      if (!this.queueId) return false
      const queueProps = this.queueProperties(this.queueId)
      return queueProps?.anonymizePhone === 'yes'
    },

    displayPhone () {
      const phone = this.attendance?.contact

      if (!phone || !this.isVoice) {
        return phone
      }

      if (this.isAnonymizationEnabled) {
        return anonymizePhone(phone)
      }

      return phone
    },

    profiler () {
      return this.profilers[this.attendance.id]
    },

    isPending () {
      const hasTree = Boolean(this.profiler?.tree)
      const hasFields = Boolean(this.profiler?.fields?.length)
      const { isSaved } = this.attendance

      if (!hasTree && !hasFields) {
        return false
      }

      return isSaved === false
    },

    unreadMessages () {
      const vm = this

      const { unreadMessages } = vm.attendance
      return unreadMessages ? `+${unreadMessages}` : ''
    },

    hasManualUnread () {
      return this.isText && Boolean(this.attendance?.manualUnread)
    },

    hasBadgeWithNumber () {
      return Boolean(this.unreadMessages) && !this.isCompleted
    },

    hasBadgeEmpty () {
      return this.hasManualUnread && !this.hasBadgeWithNumber
    },

    hasUnreadBadge () {
      return this.hasBadgeWithNumber || this.hasBadgeEmpty
    },

    unreadBadgeProps () {
      if (this.unreadMessages && !this.isCompleted) return { label: this.unreadMessages }
      return {}
    },

    canMarkAsUnread () {
      return this.isText && !this.hasUnreadBadge
    },

    isFinishedChatStatus () {
      const finishedStatuses = ['FINISHED', 'ERROR']
      return this.isText && (finishedStatuses.includes(this.attendance?.status))
    },

    isActive () {
      if (this.attendance.onConference) return true
      const attendanceId = String(this.attendance.id ?? this.attendance.temporaryId ?? '').trim()
      if (!attendanceId) return false
      return String(this.$route?.params?.attendanceId) === attendanceId
    },

    contact () {
      const { attendance } = this
      if (!attendance) return ''

      if (attendance.contact) {
        const isListenExtension = attendance.contact.includes('*99*')
        if (!isListenExtension) {
          return this.isVoice ? this.displayPhone : attendance.contact
        }
        const extension = attendance.contact.split('*99*').pop().split('@').shift()
        return `Ouvir Ramal ${extension}`
      }

      const contactInfo = parseContactInfo(attendance.info)
      if (contactInfo) return contactDisplayName(contactInfo) ?? 'Desconhecido'
      return concatName(attendance.source, getContactNumber(attendance))
    },

    contactTooltip () {
      return contactInfoTooltip(this.attendance?.info)
    },

    iconItem () {
      const { direction } = this.attendance
      if (this.isEmail) return direction === DIRECTION_OUT ? MailOutIcon : MailInIcon
      if (!this.isVoice) return direction === DIRECTION_OUT ? MessageOutIcon : MessageInIcon

      const { status } = this.attendance
      const isDiscarded = status === DISCARDED
      if (isDiscarded) return IconCallRejectedOut

      const isCompleted = status === COMPLETED
      if (direction === DIRECTION_AUTO) return isCompleted ? IconCallAnsweredAuto : IconCallOngoingAuto
      if (direction === DIRECTION_OUT) return isCompleted ? IconCallAnsweredOut : IconCallOngoingOut
      return isCompleted ? IconCallAnsweredIn : IconCallOngoingIn
    },

    colorItem () {
      const vm = this
      if (vm.isOngoing) return 'colorOngoing'

      const isDiscarded = vm.attendance.status === DISCARDED || vm.attendance.status === LOSED
      if (isDiscarded) return 'colorDiscarded'

      const isCompleted = ['FINISHED', 'AUTO_CLOSE', 'ABANDON', 'SEND_DONE', 'ANSWER_DONE', COMPLETED, 'SENDED', 'TRANSFERRED'].includes(vm.attendance.status)
      if (isCompleted) return 'colorCompleted'

      return 'is-not-active'
    },

    isCompleted () {
      return ['FINISHED', 'AUTO_CLOSE', 'ABANDON', 'ABANDONED', 'LOSED', 'ERROR', 'SEND_DONE', 'ANSWER_DONE', COMPLETED, 'SENDED', 'ONSURVEY'].includes(this.attendance.status)
    },

    initialDate () {
      if (this.attendance?.status === DISCARDED) return false
      return this.attendance?.direction === DIRECTION_IN ? this.attendance.answerAt : this.attendance.createdAt
    },

    textColor () {
      return 'textDark'
    },

    textColorPending () {
      return 'text-red-7'
    },

    bgColor () {
      return this.isActive ? 'bg-active-item' : ''
    },

    statusText () {
      return Helpers.getCallStatusLabel(this.attendance.status)
    },

    statusDirection () {
      const { attendance } = this
      const { status, direction } = attendance
      const isDiscarded = status === DISCARDED
      const isAnswering = status === ANSWERING
      const isOffering = status === OFFERING
      const isSendDone = status === SEND_DONE
      const isLosed = status === LOSED
      const isCompleted = ['FINISHED', 'AUTO_CLOSE', 'ABANDON', 'SEND_DONE', 'ANSWER_DONE', COMPLETED, 'SENDED'].includes(status)

      if (isSendDone && !direction) return 'Envio Realizado'

      if (isCompleted && direction === DIRECTION_OUT) return 'Ativa completada'
      if (isCompleted && direction === DIRECTION_IN) return 'Receptiva completada'
      if (isCompleted && direction === DIRECTION_AUTO) return 'Automática completada'

      const isTransferred = status === 'TRANSFERRED'
      if (isTransferred && direction === DIRECTION_OUT) return 'Ativa Transferida'
      if (isTransferred && direction === DIRECTION_IN) return 'Receptiva Transferida'

      const isOngoing = this.isOngoing
      if (isOngoing && direction === DIRECTION_OUT) return 'Ativa em andamento'
      if (isOngoing && direction === DIRECTION_IN) return 'Receptiva em andamento'
      if (isOngoing && direction === DIRECTION_AUTO) return 'Automática em andamento'

      if (isAnswering) return 'Resposta em andamento'
      if (isOffering) return 'Em oferecimento'

      if (isLosed && direction === DIRECTION_OUT) return 'Ativa abandonada'
      if (isLosed && direction === DIRECTION_IN) return 'Receptiva abandonada'

      return isDiscarded ? 'Ativa Descartada' : status
    },

    showStatus () {
      return this.attendance?.status === DISCARDED || !this.attendance.hangupAt
    },

    isVoice () {
      return this.attendance?.channel === VOICE_CHANNEL
    },

    isEmail () {
      return this.attendance?.channel === MAIL_CHANNEL
    },

    isText () {
      return this.attendance?.channel === TEXT_CHANNEL
    },

    isOngoing () {
      if (['FINISHED', 'AUTO_CLOSE', 'ABANDON', 'SEND_DONE', 'ANSWER_DONE', 'SENDED', 'TRANSFERRED'].includes(this.attendance.status)) return false
      const { hangupAt, finishedAt } = this.attendance
      return !hangupAt && !finishedAt
    },

    attendanceTime () {
      const { isVoice, attendance } = this
      if (attendance.channel === MAIL_CHANNEL) return (attendance.finishedAt || attendance.createdAt) * 1000
      let timestamp = isVoice ? attendance.hangupAt : attendance.finishedAt
      if (!timestamp) timestamp = [DIRECTION_IN, DIRECTION_AUTO].includes(attendance.direction) ? attendance.answerAt : attendance.createdAt
      return timestamp || attendance.createdAt
    },

    attendanceDate () {
      return new Date(this.attendanceTime).toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' })
    },

    lastMessage () {
      const lastMessage = this.attendance?.message

      const status = textStatus(lastMessage)
      if (status) return status

      const isFinished = Boolean(this.attendance?.finishedAt)
      return isFinished ? 'Finalizado' : '...'
    },

    mailStatus () {
      const attendance = this.attendance ? Object.assign({}, this.attendance) : {}
      const mailTo = Array.isArray(attendance.to) && attendance.to.length ? attendance.to[0].id : ''
      const subject = String(attendance.subject ?? '').trim().substring(0, 12)
      return `${subject}<br>${mailTo}`.trim()
    }
  },

  methods: {
    ...mapActions({
      markChatAsUnread: 'attendanceModule/markChatAsUnread',
      clearChatUnreadMark: 'attendanceModule/clearChatUnreadMark',
      sendUnreadUpdate: 'socketModule/sendUnreadUpdate'
    }),

    handleMarkAsUnread () {
      const chatId = this.attendance?.id

      if (!chatId || !this.canMarkAsUnread) return
      this.markChatAsUnread(chatId)
      this.sendUnreadUpdate({ chatId, manualUnread: true })

      console.log('Chat marcado como não lido:', chatId)
      console.log(this.isFinishedChatStatus)

      if (this.isFinishedChatStatus) {
        this.$q.notify({
          message: 'Atendimentos já finalizados ficam disponíveis somente no dia de hoje',
          textColor: 'black',
          icon: 'mdi-information-outline',
          position: 'bottom',
          timeout: 3000,
          closeBtn: true,
          classes: 'notification-popup'
        })
      }
    },

    handleMarkAsRead () {
      const chatId = this.attendance?.id
      if (!chatId || !this.hasManualUnread) return
      this.clearChatUnreadMark(chatId)
      this.sendUnreadUpdate({ chatId, manualUnread: false })
    }
  }
}

const textStatus = (lastMessage) => {
  switch (lastMessage?.type) {
    case 'TEXT': return String(lastMessage?.message || '').trim()
    case 'IMAGE': return 'Mensagem: Imagem'
    case 'AUDIO': return 'Mensagem: Áudio'
    case 'DOC': return 'Mensagem: Documento'
    case 'VIDEO': return 'Mensagem: Vídeo'
  }

  return ''
}

const concatName = (firstName, lastName) => {
  if (!lastName) return firstName
  if (!firstName) return lastName
  return `${firstName} ${lastName}`.trim()
}
</script>

<style lang="sass" scoped>
$color-ongoing: #FFAB00
$color-discarded: #E53935
$color-completed: #50C878
$color-default: #01253C

.label-contact
  font-weight: 400
  font-style: normal
  font-size: 13px
  line-height: 15px
  margin-bottom: 3px

.label-status
  width: 100%
  font-weight: 300
  font-style: normal
  font-size: 12px
  line-height: 14px
  color: rgba(38, 50, 57, 0.8)

.label-time
  font-weight: 300
  font-size: 11px
  line-height: 14px

.icon
  width: 22px
  height: 19px

.is-not-active
  path
    fill: $color-default

.item-list-container
  cursor: pointer
  background: #FFFFFF
  border: 1px solid #CDDCE5
  border-radius: 10px
  margin: 0 8px 6px
  transition: box-shadow .15s, border-color .15s
  &:hover
    box-shadow: 0 4px 12px -6px rgba(23, 105, 182, 0.35)

.bg-active-item
  background-color: rgba(23, 105, 182, 0.08)
  border-color: #1769B6

.round
  border-radius: 2px

.colorOngoing
  path
    fill: $color-ongoing

.colorDiscarded
  path
    fill: $color-discarded

.colorCompleted
  path
    fill: $color-completed

.icon-chat
  transform: scale(1.15)
  path
    stroke-width: 0.3
  &.is-not-active path
    stroke: $color-default
  &.colorOngoing path
    stroke: $color-ongoing
  &.colorDiscarded path
    stroke: $color-discarded
  &.colorCompleted path
    stroke: $color-completed

.textDark
  color: $color-default

.text-no-wrap
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.max-width-170
  max-width: 170px

.bold
  font-weight: 500

.chat-icon-heigth
  height: 22px
  width: 22px

.no-select
  user-select: none
  -webkit-user-select: none
  -moz-user-select: none
  -ms-user-select: none

.hover-actions
  display: flex
  align-items: center
  opacity: 0
  visibility: hidden
  pointer-events: none

.hover-actions.is-menu-open
  opacity: 1
  visibility: visible
  pointer-events: auto

.item-list-container:hover .hover-actions
  opacity: 1
  visibility: visible
  pointer-events: auto

.hover-actions:focus-within
  opacity: 1
  visibility: visible
  pointer-events: auto
</style>

<style lang="sass">
.notification-popup
  background-color: white
  border: 1px solid #d9d9d9
  border-radius: 20px
</style>
